/**
 * Video Tracking Hook
 *
 * Manages video analytics tracking (start, progress milestones, completion)
 * and progress updates to the backend.
 */

import { useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateProgress, getLocalizedTitle, type Material } from '@immopersian/common';
import { trackVideoStart, trackVideoProgress, trackVideoComplete } from '../../../lib/analytics';

export function useVideoTracking(material: Material, sessionId?: string) {
  const { i18n } = useTranslation();
  const updateProgress = useUpdateProgress();
  const hasStartedRef = useRef(false);
  const progressMilestonesRef = useRef<Set<number>>(new Set());

  const handlePlay = useCallback(() => {
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      trackVideoStart(material.id, getLocalizedTitle(material, i18n.language), sessionId || '');
    }
  }, [material.id, material, i18n.language, sessionId]);

  const handleProgress = useCallback(
    (progress: { currentTime: number; duration: number; percent: number }) => {
      // Track milestones (25%, 50%, 75%)
      const milestones = [25, 50, 75];
      for (const milestone of milestones) {
        if (progress.percent >= milestone && !progressMilestonesRef.current.has(milestone)) {
          progressMilestonesRef.current.add(milestone);
          trackVideoProgress(material.id, milestone, sessionId || '');

          updateProgress.mutate({
            entityType: 'material',
            entityId: material.id,
            progress: milestone,
          });
        }
      }
    },
    [material.id, sessionId, updateProgress]
  );

  const handleEnded = useCallback(() => {
    trackVideoComplete(material.id, getLocalizedTitle(material, i18n.language), sessionId || '');
    trackVideoProgress(material.id, 100, sessionId || '');

    updateProgress.mutate({
      entityType: 'material',
      entityId: material.id,
      progress: 100,
      completed: true,
    });
  }, [material.id, material, i18n.language, sessionId, updateProgress]);

  return {
    handlePlay,
    handleProgress,
    handleEnded,
  };
}
